import React, { useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import {
  Add,
  Check,
  DeleteOutline,
  EditOutlined,
  KeyboardArrowDown,
  MoreVert,
  PushPinOutlined,
} from "@mui/icons-material";
import { SaveViewButton } from "./SaveViewButton";
import { showToast } from "services/utils/Status";
import { designColors, designShell } from "services/constants/designTokens";

const triggerButtonSx = {
  minHeight: 38,
  px: 1.75,
  borderRadius: "8px",
  border: "1px solid",
  borderColor: designColors.slate200,
  backgroundColor: designColors.slate100,
  boxShadow: "none",
  color: designColors.slate800,
  fontSize: 14,
  fontWeight: 600,
  textTransform: "none",
  "&:hover": {
    backgroundColor: designColors.slate100,
  },
  "& .MuiButton-endIcon": {
    ml: 0.75,
  },
  "& .MuiSvgIcon-root": {
    color: designColors.slate800,
  },
};

const viewRowSx = (active) => ({
  px: 1,
  py: 0.5,
  minHeight: 34,
  borderRadius: "8px",
  cursor: "pointer",
  backgroundColor: active ? "#ECFDF5" : "transparent",
  border: active ? "1px solid #A7F3D0" : "1px solid transparent",
  "&:hover": {
    backgroundColor: active ? "#ECFDF5" : "#F8FAFC",
  },
});

// Shared saved-view selector UI. Screens can opt into this one-by-one while
// keeping their own saved-view persistence and default-column choices.
export const SavedViewSelector = ({
  headers = [],
  defaultHeaders = [],
  defaultView,
  savedViews = [],
  activeView = null,
  activeViewId = null,
  persistedDefaultViewId = null,
  screenAccess,
  onSelectDefaultView,
  onSelectView,
  onCreateView,
  onUpdateView,
  onDeleteView,
  onMakeDefaultView,
  defaultViewLabel = "Default View",
  checkboxSx = null,
  cancelButtonSx = null,
  submitButtonSx = null,
  // Columns that stay first, in this order, and can't be dragged.
  fixedLeadingKeys = null,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuView, setMenuView] = useState(null);
  const saveViewButtonRef = useRef(null);

  const selectedLabel = activeView?.name || defaultViewLabel;
  const menuOpen = Boolean(menuAnchorEl);
  const defaultRowId = defaultView?.id || "__default_view__";
  const defaultIsActive = activeViewId === defaultRowId;
  const sortedViews = useMemo(() => savedViews, [savedViews]);

  const ensureAccess = () => {
    if (screenAccess) return true;
    showToast("error", "Access denied: You are not authorized to save views");
    return false;
  };

  const closeAllMenus = () => {
    setMenuAnchorEl(null);
    setMenuView(null);
    setAnchorEl(null);
  };

  const closeViewMenuOnly = () => {
    setMenuAnchorEl(null);
    setMenuView(null);
  };

  const handleOpenSelector = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSelector = () => {
    setAnchorEl(null);
    setMenuAnchorEl(null);
    setMenuView(null);
  };

  const handleOpenViewMenu = (event, view) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuView(view);
  };

  const handleCloseViewMenu = () => {
    setMenuAnchorEl(null);
    setMenuView(null);
  };

  const handleAddView = (event) => {
    if (!ensureAccess()) return;
    saveViewButtonRef.current?.openCreate(event.currentTarget);
  };

  const handleEditView = () => {
    if (!menuAnchorEl || !menuView || !ensureAccess()) return;
    const nextAnchor = menuAnchorEl;
    const nextView = menuView;
    closeViewMenuOnly();
    saveViewButtonRef.current?.openEdit(nextAnchor, nextView);
  };

  const handleDeleteView = () => {
    if (!menuView || !ensureAccess()) return;
    onDeleteView?.(menuView.id);
    closeAllMenus();
  };

  const handleMakeDefaultView = () => {
    if (!menuView || !ensureAccess()) return;
    onMakeDefaultView?.(menuView.id);
    closeAllMenus();
  };

  const handleEditDefaultView = () => {
    if (!menuAnchorEl || !defaultView || !ensureAccess()) return;
    const nextAnchor = menuAnchorEl;
    closeViewMenuOnly();
    saveViewButtonRef.current?.openEdit(nextAnchor, defaultView);
  };

  return (
    <>
      <Mui.Button
        onClick={handleOpenSelector}
        endIcon={<KeyboardArrowDown fontSize="small" />}
        sx={triggerButtonSx}
      >
        {selectedLabel}
      </Mui.Button>

      <Mui.Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseSelector}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        elevation={0}
        PaperProps={{
          sx: {
            mt: 1,
            width: 280,
            borderRadius: designShell.actionMenuPaper.borderRadius,
            border: designShell.actionMenuPaper.border,
            boxShadow: designShell.actionMenuPaper.boxShadow,
            p: 1.25,
          },
        }}
      >
        <Mui.Stack spacing={1}>
          <Mui.Typography
            sx={{ fontSize: 13, fontWeight: 700, color: "#334155", px: 0.5 }}
          >
            Views
          </Mui.Typography>

          <Mui.Stack spacing={0.5}>
            <Mui.Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={viewRowSx(defaultIsActive)}
              onClick={() => {
                onSelectDefaultView?.();
                handleCloseSelector();
              }}
            >
              <Mui.Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ minWidth: 0, flex: 1 }}
              >
                <Mui.Box
                  sx={{ width: 18, display: "flex", justifyContent: "center" }}
                >
                  {defaultIsActive ? (
                    <Check sx={{ fontSize: 18, color: "#059669" }} />
                  ) : null}
                </Mui.Box>
                <Mui.Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: defaultIsActive ? 700 : 500,
                    color: "#0F172A",
                  }}
                >
                  {defaultViewLabel}
                </Mui.Typography>
              </Mui.Stack>
              <Mui.IconButton
                size="small"
                sx={{ color: "#64748B", p: 0.5 }}
                onClick={(event) =>
                  handleOpenViewMenu(event, {
                    ...(defaultView || {
                      id: "__default_view__",
                      name: defaultViewLabel,
                    }),
                    isDefaultView: true,
                  })
                }
              >
                <MoreVert fontSize="small" />
              </Mui.IconButton>
            </Mui.Stack>

            {sortedViews.map((view) => {
              const isActive = activeViewId === view.id;
              return (
                <Mui.Stack
                  key={view.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={viewRowSx(isActive)}
                  onClick={() => {
                    onSelectView?.(view.id);
                    handleCloseSelector();
                  }}
                >
                  <Mui.Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{ minWidth: 0, flex: 1 }}
                  >
                    <Mui.Box
                      sx={{ width: 18, display: "flex", justifyContent: "center" }}
                    >
                      {isActive ? (
                        <Check sx={{ fontSize: 18, color: "#059669" }} />
                      ) : null}
                    </Mui.Box>
                    <Mui.Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: isActive ? 700 : 500,
                        color: "#0F172A",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {view.name}
                    </Mui.Typography>
                  </Mui.Stack>

                  <Mui.IconButton
                    size="small"
                    sx={{ color: "#64748B", p: 0.5 }}
                    onClick={(event) => handleOpenViewMenu(event, view)}
                  >
                    <MoreVert fontSize="small" />
                  </Mui.IconButton>
                </Mui.Stack>
              );
            })}

            {sortedViews.length === 0 && (
              <Mui.Typography
                sx={{ px: 1.25, py: 0.5, fontSize: 12, color: "#64748B" }}
              >
                No custom views yet.
              </Mui.Typography>
            )}
          </Mui.Stack>

          <Mui.Divider />

          <Mui.Button
            startIcon={<Add fontSize="small" />}
            onClick={handleAddView}
            sx={{
              alignSelf: "flex-start",
              px: 0.75,
              minHeight: 28,
              borderRadius: "8px",
              color: "#0F766E",
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Add new view
          </Mui.Button>
        </Mui.Stack>
      </Mui.Popover>

      <Mui.Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleCloseViewMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: 170 } }}
      >
        <Mui.MenuItem
          onClick={menuView?.isDefaultView ? handleEditDefaultView : handleEditView}
        >
          <EditOutlined sx={{ fontSize: 18, mr: 1, color: "#64748B" }} />
          Edit
        </Mui.MenuItem>
        <Mui.MenuItem
          onClick={handleMakeDefaultView}
          disabled={!menuView || persistedDefaultViewId === menuView.id}
        >
          <PushPinOutlined sx={{ fontSize: 18, mr: 1, color: "#64748B" }} />
          Make default
        </Mui.MenuItem>
        {!menuView?.isDefaultView && (
          <Mui.MenuItem onClick={handleDeleteView}>
            <DeleteOutline sx={{ fontSize: 18, mr: 1, color: "#DC2626" }} />
            Delete
          </Mui.MenuItem>
        )}
      </Mui.Menu>

      <SaveViewButton
        ref={saveViewButtonRef}
        headers={headers}
        screenAccess={screenAccess}
        activeView={activeView}
        onCreateSavedView={onCreateView}
        onUpdateSavedView={onUpdateView}
        createInitialHeaderConfig={
          defaultView?.headerConfig?.length
            ? defaultView.headerConfig
            : defaultHeaders.map((header) => ({
                key: header.key,
                name: header.name,
              }))
        }
        fixedLeadingKeys={fixedLeadingKeys}
        hideTrigger
        createTitle="Create View"
        editTitle="Edit View"
        namePlaceholder="Enter view name"
        checkboxSx={
          checkboxSx || {
            color: designColors.teal,
            "&.Mui-checked": {
              color: designColors.teal,
            },
          }
        }
        cancelButtonSx={
          cancelButtonSx || {
            minWidth: 96,
            minHeight: 40,
            px: 2,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: 14,
            fontWeight: 600,
            color: designColors.slate800,
            backgroundColor: designColors.white,
            border: `1px solid ${designColors.borderInput}`,
            "&:hover": {
              backgroundColor: designColors.slateBg,
              borderColor: designColors.slate300,
            },
          }
        }
        submitButtonSx={
          submitButtonSx || {
            ...designShell.primaryButtonBase,
            minWidth: 110,
            minHeight: 40,
            px: 2.25,
            borderRadius: "8px",
            fontSize: 14,
            fontWeight: 600,
            "&:hover": {
              background: `linear-gradient(135deg, ${designColors.tealDarker} 0%, ${designColors.tealDark} 100%)`,
            },
          }
        }
      />
    </>
  );
};
